import { Form, Formik } from 'formik';
import React, { FC, useState } from 'react';
import * as yup from 'yup';
import { UpdatePasswordFormType } from '../../../models/update-password-form-type';
import FormErrorTypeEnum from '../../../models/form-error-type-enum';
import accountPasswordReset from '../../../services/customer-api/endpoints/account/password-reset';
import ButtonPrimary from '../../atoms/button-primary/button-primary';
import FormText from '../../atoms/form-text/form-text';
import usePushFormikFormToDataLayer from '../../../hooks/use-push-formik-form-to-data-layer/use-push-formik-form-to-data-layer';

interface Props extends UpdatePasswordFormType {
  onSuccess: () => void;
}

const UpdatePasswordForm: FC<Props> = ({
  confirmNewPasswordFieldLabel,
  confirmNewPasswordFieldMandatoryError,
  confirmNewPasswordFieldValidationError,
  existingPasswordFieldLabel,
  existingPasswordFieldMandatoryError,
  existingPasswordFieldValidationError,
  newPasswordFieldLabel,
  newPasswordFieldMandatoryError,
  newPasswordFieldValidationError,
  onSuccess,
  saveButtonText,
}: Props) => {
  const [formSuccessfullySubmitted, setFormSuccessfullySubmitted] =
    useState<boolean>(null);

  const formContext = usePushFormikFormToDataLayer(
    'Update password',
    formSuccessfullySubmitted
  );

  return (
    <Formik
      innerRef={formContext}
      initialValues={{
        existingPassword: '',
        newPassword: '',
        confirmPassword: '',
      }}
      validationSchema={yup.object({
        existingPassword: yup
          .string()
          .required(existingPasswordFieldMandatoryError)
          .trim(existingPasswordFieldMandatoryError)
          .label(existingPasswordFieldLabel),
        newPassword: yup
          .string()
          .required(newPasswordFieldMandatoryError)
          .trim(newPasswordFieldMandatoryError)
          .label(newPasswordFieldLabel),
        confirmPassword: yup
          .string()
          .oneOf(
            [yup.ref('newPassword'), null],
            'The passwords entered do not match'
          )
          .required(confirmNewPasswordFieldMandatoryError)
          .trim(confirmNewPasswordFieldMandatoryError)
          .label(confirmNewPasswordFieldLabel),
      })}
      validateOnMount
      validateOnChange
      onSubmit={async (values, { setErrors }) => {
        try {
          await accountPasswordReset(
            values.newPassword,
            values.existingPassword
          );

          setFormSuccessfullySubmitted(true);
          onSuccess();
        } catch (error) {
          if (error?.response?.status === 400) {
            setErrors({
              confirmPassword: newPasswordFieldValidationError,
              newPassword: confirmNewPasswordFieldValidationError,
            });

            return;
          }

          if (error?.response?.status === 401) {
            setErrors({
              existingPassword: existingPasswordFieldValidationError,
            });

            return;
          }

          setFormSuccessfullySubmitted(false);
        }
      }}
    >
      {({ errors, isValid, values }) => {
        const newPasswordChanged =
          values.newPassword !== values.existingPassword;

        return (
          <Form className="flex flex-col space-y-5">
            <FormText
              label={existingPasswordFieldLabel}
              name="existingPassword"
              required
              type="password"
            />

            <FormText
              help={
                errors.newPassword === newPasswordFieldValidationError
                  ? ''
                  : newPasswordFieldValidationError
              }
              label={newPasswordFieldLabel}
              name="newPassword"
              required
              type="password"
            />

            <FormText
              label={confirmNewPasswordFieldLabel}
              name="confirmPassword"
              required
              type="password"
            />

            {formSuccessfullySubmitted === false && (
              <span className="block mt-1 text-sm text-invalid">
                {FormErrorTypeEnum.Unknown}
              </span>
            )}

            <div className="flex justify-center py-component-spacing-mobile lg:py-component-spacing-content">
              <ButtonPrimary
                buttonType="submit"
                disabled={
                  (!isValid &&
                    errors.confirmPassword !==
                      'The passwords entered do not match') ||
                  !newPasswordChanged
                }
                name={saveButtonText}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default UpdatePasswordForm;
